import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Heart, Feather, Coffee } from "lucide-react";
import PageHero from "@/components/site/PageHero";

const timeline = [
  { year: "Kindheit", title: "Erste Fragen", text: "Hautprobleme, Allergien, hormonelle Themen. Viele Behandlungen — wenig Antworten." },
  { year: "2016", title: "Begegnung mit Medical Medium", text: "Der Ansatz von Anthony William verändert meine Sicht auf Gesundheit grundlegend." },
  { year: "2018", title: "Start der Begleitung", text: "Ich beginne, Menschen mit meinem Wissen zu begleiten — im vertrauten Kreis zuerst." },
  { year: "2021", title: "DVAG-Zertifizierung", text: "Ich werde Vermögensberaterin der DVAG, um Finanzen genauso fundiert zu begleiten." },
  { year: "Heute", title: "Zwei Bereiche, ein Weg", text: "Ich verbinde Gesundheits- und Finanzbegleitung — weil beides zusammengehört." },
];

const values = [
  { Icon: Heart, title: "Ehrlich", text: "Ich sage, was ich sehe — auch wenn's mal unbequem ist." },
  { Icon: Feather, title: "Sanft", text: "Kein Druck, kein Dogma. Dein Tempo zählt." },
  { Icon: Sparkles, title: "Fundiert", text: "Hinter jeder Empfehlung steht Wissen und Erfahrung." },
  { Icon: Coffee, title: "Nahbar", text: "Ein Gespräch fühlt sich mehr nach Küche als nach Büro an." },
];

export default function AboutPage() {
  return (
    <>
      <PageHero
        chapter="III"
        eyebrow="Über mich"
        title="Hallo,"
        italic="ich bin Xenia."
        intro="Meine eigene Geschichte hat mich zu dem gebracht, was ich heute mache: die Verbindung aus Gesundheit und Finanzen. Hier erzähle ich dir, wie das kam."
        tone="honey"
        image="/images/xenia-portrait.jpg"
        imagePosition="top"
      />

      {/* Story */}
      <section className="relative py-24 md:py-32 bg-bone-100">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 space-y-6 text-ink-700 text-[18px] leading-[1.8]">
          <p className="font-display italic text-[2rem] md:text-[2.5rem] leading-[1.15] text-ink-900 not-italic">
            <span className="text-sage-500 italic">„Gesundheit und Finanzen</span> sind zwei Systeme, die dasselbe wollen — dass du dich sicher fühlst."
          </p>
          <p>
            Schon als Kind hatte ich mit gesundheitlichen Themen zu tun: Hautprobleme, Allergien, hormonelle Schwierigkeiten. Vieles wurde behandelt — aber nie wirklich verstanden. Ich habe früh gelernt, dass der Körper oft mehr weiß, als wir ihm zutrauen.
          </p>
          <p>
            Als ich Mama wurde, wurde diese Suche durch die Beschwerden meines Sohnes noch intensiver. Sie führte mich schließlich zu Anthony William (Medical Medium) – und veränderte meine Sicht auf Gesundheit, Ernährung und Wohlbefinden nachhaltig.
          </p>
          <p>
            Parallel dazu habe ich beim Aufbau meiner Selbstständigkeit erlebt, wie wichtig ein anderes Thema ist: Finanzen. Nicht nur im Sinne von Geld, sondern im Sinne von Klarheit, Struktur und Sicherheit.
          </p>
          <p>
            Heute begleite ich Menschen in beiden Bereichen – ruhig, verständlich und Schritt für Schritt.
          </p>
        </div>
      </section>

      {/* Timeline */}
      <section className="relative py-24 md:py-32 bg-honey-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="mb-14">
            <div className="flex items-center gap-4">
              <span className="font-mono-fine text-[11px] tracking-[0.28em] uppercase text-ink-500">§ 01</span>
              <span className="h-[1px] w-10 bg-honey-400" />
              <span className="font-mono-fine text-[11px] tracking-[0.28em] uppercase text-honey-600">Mein Weg</span>
            </div>
            <h2 className="mt-6 font-display font-light text-4xl md:text-5xl lg:text-[3.75rem] leading-[0.98] tracking-[-0.035em] text-ink-900">
              Stationen, die mich <span className="italic font-normal">geprägt haben</span>.
            </h2>
          </div>

          <ol className="relative space-y-0">
            <div aria-hidden className="absolute left-[30px] md:left-[90px] top-2 bottom-2 w-[2px] bg-honey-200" />
            {timeline.map((t) => (
              <li key={t.year} className="relative pl-[70px] md:pl-[140px] py-6">
                <span className="absolute left-[18px] md:left-[78px] top-7 w-6 h-6 rounded-full bg-honey-400 border-4 border-honey-50" />
                <div className="absolute left-0 top-7 md:w-[90px] font-mono-fine text-[11px] tracking-[0.2em] uppercase text-honey-600 md:text-right pr-4 hidden md:block">
                  {t.year}
                </div>
                <div className="md:hidden font-mono-fine text-[11px] tracking-[0.2em] uppercase text-honey-600 mb-2">{t.year}</div>
                <h3 className="font-display text-[1.6rem] text-ink-900">{t.title}</h3>
                <p className="mt-2 text-ink-700 leading-relaxed max-w-lg">{t.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Values */}
      <section className="relative py-24 md:py-32 bg-bone-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <div className="flex items-center gap-4">
              <span className="font-mono-fine text-[11px] tracking-[0.28em] uppercase text-ink-500">§ 02</span>
              <span className="h-[1px] w-10 bg-sage-400" />
              <span className="font-mono-fine text-[11px] tracking-[0.28em] uppercase text-sage-600">Werte</span>
            </div>
            <h2 className="mt-6 font-display font-light text-4xl md:text-5xl lg:text-[3.75rem] leading-[0.98] tracking-[-0.035em] text-ink-900">
              Wofür ich <span className="italic font-normal">stehe</span>.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map(({ Icon, title, text }) => (
              <div key={title} className="bg-sage-50 border border-sage-200 rounded-[1.5rem] p-7">
                <div className="w-12 h-12 rounded-full bg-sage-300 text-sage-700 flex items-center justify-center">
                  <Icon strokeWidth={1.75} className="w-5 h-5" />
                </div>
                <h3 className="mt-5 font-display text-[1.4rem] text-ink-900">{title}</h3>
                <p className="mt-2 text-ink-700 text-[14.5px] leading-relaxed">{text}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-3 pl-6 pr-2 h-14 rounded-full bg-sage-500 hover:bg-sage-600 text-bone-50 text-[14px] tracking-wide transition-all group"
            >
              Lass uns kennenlernen
              <span className="w-11 h-11 rounded-full bg-honey-400 text-ink-900 flex items-center justify-center transition-transform group-hover:rotate-45">
                <ArrowUpRight strokeWidth={1.75} className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
